import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { EmptyState } from './EmptyState'
import { plural } from '@/lib/format'

export type SortDirection = 'asc' | 'desc'

export interface SortState {
  key: string
  direction: SortDirection
}

export interface Column<T> {
  /** Klucz kolumny — używany też jako klucz sortowania wysyłany do API. */
  key: string
  header: ReactNode
  /** Renderer komórki. Gdy brak — użyty zostanie `accessor`. */
  render?: (row: T, index: number) => ReactNode
  /** Wartość do sortowania po stronie klienta i domyślnej treści komórki. */
  accessor?: (row: T) => string | number | Date | null | undefined
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  /** np. '160px' albo '20%' */
  width?: string
  className?: string
  headerClassName?: string
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T, index: number) => string | number
  onRowClick?: (row: T, index: number) => void

  /** Sortowanie kontrolowane (serwerowe). Bez tego sortuje się po stronie klienta. */
  sort?: SortState | null
  onSortChange?: (sort: SortState) => void
  defaultSort?: SortState

  /** Paginacja serwerowa: podaj `total` + `page` + `onPageChange` (patrz Paginated<T>). */
  total?: number
  page?: number
  pageSize?: number
  onPageChange?: (page: number) => void

  loading?: boolean
  empty?: ReactNode
  /** Węższe wiersze (gęste zestawienia). */
  dense?: boolean
  className?: string
  /** Podświetlenie wiersza (np. zaznaczony dokument). */
  isRowActive?: (row: T, index: number) => boolean
}

const ALIGN = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const

function compare(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : 1
  if (b === null || b === undefined) return -1
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'pl')
}

/**
 * Tabela w stylu „Rejestr spraw": wersalikowa główka, cienkie separatory,
 * klikalne wiersze, sortowanie i paginacja (klient albo serwer).
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  sort,
  onSortChange,
  defaultSort,
  total,
  page,
  pageSize = 50,
  onPageChange,
  loading = false,
  empty,
  dense = false,
  className,
  isRowActive,
}: DataTableProps<T>) {
  const [internalSort, setInternalSort] = useState<SortState | null>(defaultSort ?? null)
  const [internalPage, setInternalPage] = useState(1)

  const serverSorted = sort !== undefined
  const activeSort = serverSorted ? sort : internalSort

  const serverPaginated = total !== undefined && onPageChange !== undefined
  const currentPage = serverPaginated ? (page ?? 1) : internalPage

  const sortedRows = useMemo(() => {
    if (serverSorted || !activeSort) return rows
    const column = columns.find((c) => c.key === activeSort.key)
    if (!column?.accessor) return rows
    const accessor = column.accessor
    const factor = activeSort.direction === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => compare(accessor(a), accessor(b)) * factor)
  }, [rows, columns, activeSort, serverSorted])

  const pagedRows = useMemo(() => {
    if (serverPaginated) return sortedRows
    if (sortedRows.length <= pageSize) return sortedRows
    const start = (internalPage - 1) * pageSize
    return sortedRows.slice(start, start + pageSize)
  }, [sortedRows, serverPaginated, internalPage, pageSize])

  const rowCount = serverPaginated ? (total ?? 0) : sortedRows.length
  const pageCount = Math.max(1, Math.ceil(rowCount / pageSize))

  const handleSort = (column: Column<T>) => {
    if (!column.sortable) return
    const direction: SortDirection =
      activeSort?.key === column.key && activeSort.direction === 'asc' ? 'desc' : 'asc'
    const next: SortState = { key: column.key, direction }
    if (!serverSorted) setInternalSort(next)
    onSortChange?.(next)
  }

  const changePage = (next: number) => {
    const clamped = Math.min(Math.max(1, next), pageCount)
    if (serverPaginated) onPageChange?.(clamped)
    else setInternalPage(clamped)
  }

  return (
    <div className={cn('am-card overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-line-soft">
              {columns.map((column) => {
                const isSorted = activeSort?.key === column.key
                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    className={cn(
                      // Nagłówki są jednoliniowe jak w mockupach („WYKRYTY TERMIN" nie łamie się).
                      't-label whitespace-nowrap bg-card px-4 py-3 font-semibold',
                      ALIGN[column.align ?? 'left'],
                      column.sortable && 'group cursor-pointer select-none hover:text-ink',
                      column.headerClassName,
                    )}
                    onClick={() => handleSort(column)}
                    aria-sort={isSorted ? (activeSort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {column.header}
                      {column.sortable ? (
                        <span
                          className={cn(
                            'text-[10px] leading-none',
                            isSorted ? 'text-ink' : 'text-[#b9c4cc] opacity-0 transition-opacity group-hover:opacity-100',
                          )}
                        >
                          {isSorted ? (activeSort.direction === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      ) : null}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-line-soft last:border-0">
                    {columns.map((column) => (
                      <td key={column.key} className={cn('px-4', dense ? 'py-2.5' : 'py-4')}>
                        <span className="block h-3 w-full max-w-[180px] animate-pulse rounded bg-[#eceff2]" />
                      </td>
                    ))}
                  </tr>
                ))
              : pagedRows.map((row, index) => (
                  <tr
                    key={rowKey(row, index)}
                    onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.key === 'Enter') onRowClick(row, index)
                          }
                        : undefined
                    }
                    className={cn(
                      'border-b border-line-soft last:border-0',
                      onRowClick && 'cursor-pointer transition-colors duration-fast hover:bg-rowhover focus-visible:am-focus focus:outline-none',
                      isRowActive?.(row, index) && 'bg-[#f2f6f9]',
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          'px-4 align-middle text-base',
                          dense ? 'py-2' : 'py-3',
                          ALIGN[column.align ?? 'left'],
                          column.className,
                        )}
                      >
                        {column.render
                          ? column.render(row, index)
                          : column.accessor
                            ? String(column.accessor(row) ?? '—')
                            : null}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {!loading && pagedRows.length === 0 ? (
        empty ?? <EmptyState title="Brak danych" description="Nie ma pozycji spełniających kryteria." />
      ) : null}

      {pageCount > 1 ? (
        <div className="flex items-center justify-between border-t border-line-soft px-4 py-2.5">
          <span className="text-micro text-muted">
            {rowCount} {plural(rowCount, 'pozycja', 'pozycje', 'pozycji')} · strona {currentPage} z {pageCount}
          </span>
          <div className="flex items-center gap-1.5">
            <PagerButton disabled={currentPage <= 1} onClick={() => changePage(currentPage - 1)}>
              ‹ Poprzednia
            </PagerButton>
            <PagerButton disabled={currentPage >= pageCount} onClick={() => changePage(currentPage + 1)}>
              Następna ›
            </PagerButton>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function PagerButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="h-[26px] rounded border border-line bg-card px-2.5 text-micro text-ink transition-colors duration-fast hover:bg-rowhover disabled:opacity-40 disabled:hover:bg-card"
    >
      {children}
    </button>
  )
}
